import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { FaUserFriends, FaPlus, FaTrashAlt, FaExclamationTriangle, FaClipboardList } from "react-icons/fa";
import Modal from "../Modal";

export default function VisitacionSection({ fecha }) {
  const { token } = useContext(AuthContext);

  const [visitacion, setVisitacion] = useState([]);
  const [miembros, setMiembros] = useState([]);
  const [miembroSeleccionado, setMiembroSeleccionado] = useState("");
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });
  const [porEliminar, setPorEliminar] = useState(null);

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const formatearFecha = (fecha) => new Date(fecha).toLocaleDateString("es-MX", { timeZone: "UTC" });

  // ============================
  // CARGAR VISITACIÓN
  // ============================
  const cargarVisitacion = async () => {
    try {
      const res = await fetch(
        `/api/visitacion/${fecha}`,
        {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      setVisitacion(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  };

  // ============================
  // CARGAR MIEMBROS
  // ============================
  const cargarMiembros = async () => {
    try {
      const res = await fetch(`/api/miembros`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMiembros(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    cargarVisitacion();
    cargarMiembros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  // ============================
  // AGREGAR
  // ============================
  const agregar = async () => {
    if (!miembroSeleccionado) {
      mostrarToast("Selecciona un miembro", "error");
      return;
    }

    try {
      const res = await fetch(`/api/visitacion`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id_miembro: miembroSeleccionado,
          fecha,
        }),
      });

      if (!res.ok) {
        mostrarToast("Error agregando a visitación", "error");
        return;
      }

      mostrarToast("Agregado a visitación");
      setMiembroSeleccionado("");
      cargarVisitacion();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  // ============================
  // ELIMINAR
  // ============================
  const confirmarEliminar = async () => {
    if (!porEliminar) return;

    try {
      const res = await fetch(`/api/visitacion/${porEliminar.id_visitacion}`, {
        method: "DELETE",
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        mostrarToast("Error quitando el registro", "error");
        return;
      }

      mostrarToast("Registro quitado de visitación");
      cargarVisitacion();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    } finally {
      setPorEliminar(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800 mb-4">
        <FaClipboardList className="text-indigo-600" /> Visitación
      </h2>

      {/* Selector */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={miembroSeleccionado}
          onChange={(e) => setMiembroSeleccionado(e.target.value)}
          className="flex-1 min-w-[220px] border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
        >
          <option value="">Seleccionar miembro</option>
          {miembros.map((m) => (
            <option key={m.id_miembro} value={m.id_miembro}>
              {m.nombre} {m.paterno}
            </option>
          ))}
        </select>

        <button
          onClick={agregar}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaPlus className="text-xs" /> Agregar
        </button>
      </div>

      {/* Tabla */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Nombre</th>
              <th className="px-4 py-3 text-left font-semibold">Fecha</th>
              <th className="px-4 py-3 text-right font-semibold">Acción</th>
            </tr>
          </thead>

          <tbody>
            {visitacion.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center py-8 text-slate-400">
                  Sin registros
                </td>
              </tr>
            ) : (
              visitacion.map((v) => (
                <tr key={v.id_visitacion} className="border-t border-slate-100 hover:bg-slate-50 transition">
                  <td className="px-4 py-2.5">
                    <span className="flex items-center gap-2 font-medium text-slate-800">
                      <span className="w-6 h-6 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                        <FaUserFriends className="text-[10px]" />
                      </span>
                      {v.nombre} {v.paterno}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{formatearFecha(v.fecha)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      onClick={() => setPorEliminar(v)}
                      title="Quitar"
                      className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                    >
                      <FaTrashAlt className="text-xs" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL CONFIRMAR ELIMINAR */}
      <Modal open={!!porEliminar} onClose={() => setPorEliminar(null)} maxWidth="max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mx-auto mb-4">
            <FaExclamationTriangle />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">Quitar de visitación</h3>
          <p className="text-sm text-slate-500 mb-6">
            ¿Quitar a <b>{porEliminar?.nombre} {porEliminar?.paterno}</b> de la lista de visitación del {porEliminar ? formatearFecha(porEliminar.fecha) : ""}?
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPorEliminar(null)}
              className="w-full px-4 py-2 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarEliminar}
              className="w-full px-4 py-2 text-sm rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-medium transition"
            >
              Quitar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
